import { Plus } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSelector } from 'react-redux';

import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { AnnouncedTeam } from 'modules/teams/domain/interfaces/announced-team';
import { getChampionshipTeamsV2 } from 'modules/teams/store/championship-teams/actions';
import {
  announcedChampionshipTeamsDataSelector,
  announcedChampionshipTeamsIsLoadingSelector,
} from 'modules/teams/store/championship-teams/selectors';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useAppDispatch } from 'store/hooks';
import { theme } from 'styles/theme/theme';
import { UDEntityList, UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import AddTeamsModal from './add-teams-modal';
import ApplicationStatusChips, { ApplicationStatusChipOption } from './application-status-chips';
import TeamApplicationRow from './team-application-row';

/** Фильтр статуса. «Все» — значение по умолчанию: принятая команда не должна пропадать с экрана. */
type TeamsFilter = 'all' | AnnouncedTeamStatus.PENDING | AnnouncedTeamStatus.ACCEPTED | AnnouncedTeamStatus.DECLINED;

/** Тот же дефолт лимита, что и в v1 и на экране состава: настройка турнира может быть не заполнена. */
const DEFAULT_MAX_PLAYERS = 28;

interface ApplicationsTeamsScreenProps {
  championshipId: number;
}

/** `ANNOUNCED` и `PENDING` — одно и то же состояние «решения ещё нет» (как в v1). */
const isPending = (status: AnnouncedTeamStatus) => status === AnnouncedTeamStatus.PENDING || status === AnnouncedTeamStatus.ANNOUNCED;

const matchesFilter = (team: AnnouncedTeam, filter: TeamsFilter) => {
  if (filter === 'all') return true;
  if (filter === AnnouncedTeamStatus.PENDING) return isPending(team.status);
  return team.status === filter;
};

/**
 * Экран списка заявленных команд, второй флоу (v2).
 *
 * Живёт по тому же адресу, что и `championship-teams/championship-applications.tsx`;
 * какой из двух рисуется, решает фиче-флаг `applicationsFlowV2` в
 * `championship-applications-page.tsx`. Старый экран не трогаем — иначе сравнивать
 * конверсию будет не с чем.
 *
 * Отличий от v1 три, и все три — про «что делать дальше»: фильтр по умолчанию «Все»
 * (в v1 «Незаявленные», из-за чего принятая команда исчезала с экрана), пустое состояние
 * с кнопкой вместо трёх нулей и «Список пуст», и объяснение сверху, из каких шагов
 * состоит заявка.
 */
const ApplicationsTeamsScreen = ({ championshipId }: ApplicationsTeamsScreenProps) => {
  const { t } = useLingui();
  const dispatch = useAppDispatch();

  const [filter, setFilter] = useState<TeamsFilter>('all');
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isError, setIsError] = useState(false);

  const isLoading = useSelector(announcedChampionshipTeamsIsLoadingSelector);
  const announcedTeams = useSelector(announcedChampionshipTeamsDataSelector(championshipId));
  const settings = useSelector(championshipSettingsSelector(championshipId));

  const maxPlayers = settings?.applicationMaxPlayers ?? DEFAULT_MAX_PLAYERS;

  useEffect(() => {
    if (announcedTeams) {
      return;
    }

    // Ошибку загрузки слайс не хранит (`championship-teams/index.ts` только гасит
    // `isLoading` в `rejected`), поэтому ветку ошибки экран держит у себя.
    setIsError(false);
    dispatch(getChampionshipTeamsV2({ championshipId }))
      .unwrap()
      .catch(() => setIsError(true));
  }, [announcedTeams, championshipId, dispatch]);

  const teams = useMemo(() => Object.values(announcedTeams || {}), [announcedTeams]);

  const countByFilter = useMemo(
    () =>
      teams.reduce<Record<TeamsFilter, number>>(
        (result, team) => {
          result.all += 1;
          if (isPending(team.status)) result[AnnouncedTeamStatus.PENDING] += 1;
          if (team.status === AnnouncedTeamStatus.ACCEPTED) result[AnnouncedTeamStatus.ACCEPTED] += 1;
          if (team.status === AnnouncedTeamStatus.DECLINED) result[AnnouncedTeamStatus.DECLINED] += 1;
          return result;
        },
        {
          all: 0,
          [AnnouncedTeamStatus.PENDING]: 0,
          [AnnouncedTeamStatus.ACCEPTED]: 0,
          [AnnouncedTeamStatus.DECLINED]: 0,
        },
      ),
    [teams],
  );

  const visibleTeams = useMemo(() => {
    const query = search.trim().toLowerCase();

    return teams.filter((team) => matchesFilter(team, filter) && team.name.toLowerCase().includes(query));
  }, [teams, filter, search]);

  // Ветку рендера выбираем по **отфильтрованному** списку: иначе «ничего не найдено»
  // никогда не покажется (`.claude/rules/conventions.md`).
  const state = useMemo(
    () =>
      deriveAsyncState({
        itemsCount: visibleTeams.length,
        hasCurrentData: Boolean(announcedTeams),
        isFetching: isLoading,
        isError,
      }),
    [visibleTeams.length, announcedTeams, isLoading, isError],
  );

  const openAdding = useCallback(() => setIsAdding(true), []);
  const closeAdding = useCallback(() => setIsAdding(false), []);

  const addButton = useMemo(
    () => (
      <UDButton className="flex items-center" variant="dark" onClick={openAdding}>
        <Plus size={16} className="mr-2" color={theme.colors.AccentNaplesYellow} />
        <span>{t`Добавить команду`}</span>
      </UDButton>
    ),
    [t, openAdding],
  );

  const chipOptions = useMemo<ApplicationStatusChipOption<TeamsFilter>[]>(
    () => [
      { value: 'all', label: t`Все`, count: countByFilter.all },
      {
        value: AnnouncedTeamStatus.PENDING,
        label: t`Ожидают решения`,
        count: countByFilter[AnnouncedTeamStatus.PENDING],
        tone: 'pending',
      },
      {
        value: AnnouncedTeamStatus.ACCEPTED,
        label: t`Заявлены`,
        count: countByFilter[AnnouncedTeamStatus.ACCEPTED],
        tone: 'accepted',
      },
      {
        value: AnnouncedTeamStatus.DECLINED,
        label: t`Отклонены`,
        count: countByFilter[AnnouncedTeamStatus.DECLINED],
        tone: 'declined',
      },
    ],
    [t, countByFilter],
  );

  const renderTeam = useCallback(
    (team: AnnouncedTeam, index: number) => <TeamApplicationRow team={team} index={index + 1} maxPlayers={maxPlayers} />,
    [maxPlayers],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 py-4" data-testid="applications-teams-screen-v2">
      <UDText type="footnote" className="text-surfaceRomanSilver30 mb-4">
        {t`Заявка команды — это два шага: сначала добавьте команду в турнир, затем заявьте её игроков.`}
      </UDText>

      {isAdding && <AddTeamsModal championshipId={championshipId} existTeamsId={teams.map((team) => team.id)} onClose={closeAdding} />}

      <UDEntityList<AnnouncedTeam>
        items={visibleTeams}
        renderItem={renderTeam}
        getItemKey={(team) => team.id}
        state={state}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по команде`}
        // Чипы не рисуем, пока команд нет вовсе: четыре нуля — ровно то, чем встречал v1.
        extraFilters={
          teams.length > 0 ? <ApplicationStatusChips options={chipOptions} value={filter} onChange={setFilter} /> : undefined
        }
        // Кнопка либо в тулбаре, либо в пустом состоянии, но не в обоих сразу.
        actions={teams.length > 0 ? addButton : undefined}
        emptyText={t`В турнире пока нет команд`}
        emptySubtitle={t`Добавьте команду — дальше останется заявить её игроков.`}
        emptyAction={addButton}
        notFoundText={t`Команды не найдены`}
        errorText={t`Не удалось загрузить заявки команд`}
      />
    </div>
  );
};

export default ApplicationsTeamsScreen;
